/* assets/accessibility-rebuy-cart-focus.js
 *
 * Re-scans the cart drawer's focus-trap tabbable list when Rebuy Smart Cart
 * lifecycle events fire, so Rebuy-injected interactive elements (checkout
 * button, line-item quantity +/- controls, remove buttons, in-drawer upsells)
 * become keyboard-reachable.
 *
 * WHY
 * ---
 * Impact's cart-drawer uses the focus-trap library (theme.js:1538). The trap
 * computes its tabbable-element list at activation time. Rebuy Smart Cart
 * hydrates its drawer DOM asynchronously AFTER the trap activates; since
 * focus-trap doesn't auto-scan DOM changes, Rebuy's injected interactive
 * elements never enter the tabbable set. Keyboard-only users can Tab only
 * to the login link and close-X, unable to reach the checkout button. See
 * docs/accessibility/vendor-escalations.md R3 and
 * docs/accessibility/test-evidence/manual-keyboard/walkthrough-results.md
 * page 4.
 *
 * APPROACH
 * --------
 * Rebuy emits lifecycle events when the merchant enables them in admin
 * (Rebuy → Smart Cart → Event Callbacks). We hook the ones that signal
 * "DOM may have changed inside the cart":
 *   - rebuy:smartcart.ready   — first hydration finished
 *   - rebuy:smartcart.show    — drawer opened (subsequent opens may re-render)
 *   - rebuy:cart.lineitem.*   — user interacted, items added/changed/removed
 *
 * On each signal we ask the drawer's focus-trap to rescan its container for
 * tabbable elements. focus-trap v6+ exposes `updateContainerElements()`; we
 * feature-detect and fall back to deactivate-then-reactivate if not present.
 *
 * REQUIRES (MERCHANT ADMIN)
 * -------------------------
 * Enable these checkboxes in Rebuy admin → Smart Cart → Event Callbacks:
 *   - Ready Callback
 *   - Show Callback
 *   - LineItemIncrease Callback
 *   - LineItemDecrease Callback
 *   - LineItemRemoved Callback
 * Without these enabled, Rebuy won't fire the events and this script is a
 * no-op.
 *
 * WCAG 2.1.1 Keyboard. Related: vendor-escalations.md R3 escalation draft.
 */

(function () {
  'use strict';

  // Best-effort lookup of a drawer with an active focus-trap. We target the
  // theme's #cart-drawer first (Impact's cart container); if Rebuy injects a
  // different drawer element, extend this list as needed.
  function findActiveDrawer() {
    const candidates = [document.getElementById('cart-drawer')];
    for (const el of candidates) {
      if (el && el._focusTrap && el.hasAttribute('open')) return el;
    }
    return null;
  }

  function refreshTrap() {
    const drawer = findActiveDrawer();
    if (!drawer) return;
    const trap = drawer._focusTrap;
    if (!trap) return;

    if (typeof trap.updateContainerElements === 'function') {
      // focus-trap v6+ — preferred path, preserves current focus.
      try {
        trap.updateContainerElements(drawer);
      } catch (err) {
        // Swallow — trap may be mid-lifecycle. Fall through to the
        // deactivate/reactivate fallback on next event if persistent.
        console.warn('[a11y] Rebuy drawer trap refresh failed:', err);
      }
      return;
    }

    // Fallback for older focus-trap: recreate the tabbable list by
    // deactivating without returning focus and reactivating in place.
    try {
      trap.deactivate({ returnFocus: false });
      trap.activate();
    } catch (err) {
      console.warn('[a11y] Rebuy drawer trap reactivate fallback failed:', err);
    }
  }

  const EVENTS = [
    'rebuy:smartcart.ready',
    'rebuy:smartcart.show',
    'rebuy:cart.lineitem.increase',
    'rebuy:cart.lineitem.decrease',
    'rebuy:cart.lineitem.removed',
  ];

  for (const name of EVENTS) {
    document.addEventListener(name, refreshTrap);
  }
})();
